import {
    CountryCallingCode,
    CountryCode,
    formatIncompletePhoneNumber,
    getCountries,
    getCountryCallingCode,
    parseIncompletePhoneNumber
} from "libphonenumber-js";

import {Dropdown, DropdownMenu, InputGroup, InputGroupDropdownButton, MenuOption} from "@gs-ux-uitoolkit-react/input";
import {Text} from "@gs-ux-uitoolkit-react/text";
import {ChangeEvent, FocusEventHandler, memo, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {memoize} from "lodash";

type Props = {
    onCountryCodeChange: (cc: CountryCode) => void;
    countryCode: CountryCode;
    name: string;
    helperText?: string;
    error?: string;
    disabled?: boolean;
    value?: string;
    setValue: (newVal: string) => void;
    onBlur?: () => void;
};

const memoizedFormatNumber = memoize(formatIncompletePhoneNumber)
const memoizedParse = memoize(parseIncompletePhoneNumber)
export const FormPhoneInput = memo(function ({
                                                 countryCode,
                                                 onCountryCodeChange,
                                                 name,
                                                 helperText,
                                                 disabled,
                                                 error,
                                                 setValue,
                                                 value,
                                                 onBlur
                                             }: Props) {
    const [focused, setFocused] = useState(false);
    const countryCallingCode = useMemo(() => getCountryCallingCode(countryCode), [countryCode]);

    /**
     * if value already has correct country calling code, format it normally
     * if value doesn't have correct country calling code (input is blank), add it before formatting
     */
    const inputValue = useMemo(() => value?.substring(0, countryCallingCode.length) === countryCallingCode ? memoizedFormatNumber(value || "", countryCode).slice(countryCallingCode.length) : memoizedFormatNumber(
        countryCallingCode + value?.toString() || "", countryCode
    ).slice(countryCallingCode.length), [countryCallingCode, countryCode, value]);

    const setInputValue = useCallback((newVal: string) => setValue(newVal), [setValue]);


    return (
        <div className={`relative ${error ? "mb-3" : ""}`}>
            <InputGroup status={error ? "error" : undefined}>
                {/* <Dropdown>
                    <InputGroupDropdownButton
                        emphasis="minimal"
                        disabled={disabled}
                        classes={{
                            singleButton: disabled ? '!border-gray-20' : focused ? "!border-blue-60" : "!border-gray-50"
                        }}
                    >
                        {countryCode}
                    </InputGroupDropdownButton>
                    <DropdownMenu className="hidden">
                        {getCountries().map((countryCode) => (
                            <MenuOption key={countryCode} onSelect={() => onCountryCodeChange(countryCode)}>
                                {countryCode}
                            </MenuOption>
                        ))}
                    </DropdownMenu>
                </Dropdown> */}
                <div className="flex items-center h-[32px] mt-[-1px] w-full">
                    <MemoizedInput
                        disabled={disabled}
                        name={name}
                        focused={focused}
                        setFocused={setFocused}
                        inputValue={inputValue}
                        setValue={setInputValue}
                        countryCallingCode={countryCallingCode}
                        countryCode={countryCode}
                        onBlur={onBlur}
                    />
                </div>
            </InputGroup>
            {error ? (
                <Text typography="textCaption01Regular" color="red060"
                      className={`mt-1 absolute ${helperText ? "-bottom-3" : "-bottom-5"}`}>
                    {error}
                </Text>
            ) : null}
            {helperText && (
                <Text typography="textBody04Regular" color="colorTextNeutralBold">{helperText}</Text>
            )}
        </div>
    );
}, (prevProps, nextProps) => {
    return prevProps.value === nextProps.value &&
        prevProps.error === nextProps.error &&
        prevProps.name === nextProps.name &&
        prevProps.setValue === nextProps.setValue &&
        prevProps.onBlur === nextProps.onBlur &&
        prevProps.countryCode === nextProps.countryCode &&
        prevProps.disabled === nextProps.disabled
})

type CustomInputProps = {
    name: string;
    focused: boolean;
    setFocused: (isFocused: boolean) => void;
    onBlur?: FocusEventHandler<HTMLInputElement>;
    inputValue: string;
    countryCallingCode: CountryCallingCode;
    countryCode: CountryCode;
    setValue: (newVal: string) => void;
    disabled?: boolean;
}

const MemoizedInput = memo(function ({
                                         name,
                                         setFocused,
                                         focused,
                                         countryCallingCode,
                                         onBlur,
                                         disabled,
                                         inputValue,
                                         countryCode,
                                         setValue
                                     }: CustomInputProps) {
    const [cursor, setCursor] = useState<null | number>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    useEffect(() => {
        const input = inputRef.current;
        if (input && cursor !== null && input.selectionStart !== cursor) input.setSelectionRange(cursor, cursor);
    }, [inputRef, cursor, inputValue]);

    const currLength = inputValue.replace(/(\s|\(|\)|-)/g, "").length;
    const handleOnChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        const inputTyped = e.currentTarget.value;
        const cleanInputValue = inputTyped.replace(/(\s|\(|\)|-)/g, "");

        const addingChar = currLength < cleanInputValue.length;
        const deletingChar = currLength > cleanInputValue.length;
        // prevents country code from being deleted
        // if (cleanInputValue === "") return;
        const newInputValue = memoizedParse(inputTyped);
        const formattedVersion = newInputValue?.substring(0, countryCallingCode.length) === countryCallingCode ? memoizedFormatNumber(newInputValue || "", countryCode).slice(countryCallingCode.length) : memoizedFormatNumber(
            countryCallingCode + newInputValue?.toString() || "", countryCode
        ).slice(countryCallingCode.length);
        const formattedSelection = formattedVersion[e.currentTarget.selectionStart || 0];
        const inputTypedSelection = inputTyped[e.currentTarget.selectionStart || 0];
        if (deletingChar && (inputTypedSelection === formattedSelection || inputTypedSelection === ")" || inputTypedSelection === "-")) {
            setCursor(e.currentTarget.selectionStart);
        } else if (inputTypedSelection === "(") setCursor((e.currentTarget.selectionStart || 0) + 1);
        // cursor was at the end of the input
        else if (e.currentTarget.selectionStart === e.currentTarget.value.length)
            // cursor at the end of input and last char is closed parens, then cursor will skip it
            if (formattedSelection === ")")
                setCursor(formattedVersion.length - 1);
            else
                setCursor(formattedVersion.length);
        // cursor was at the start
        else if (e.currentTarget.selectionStart !== null && e.currentTarget.selectionStart < 4 && countryCallingCode + inputTyped[e.currentTarget.selectionStart] === newInputValue?.substring(0, countryCallingCode.length + 1)) {
            if (deletingChar)
                setCursor(countryCallingCode.length);
            else if (addingChar)
                setCursor(countryCallingCode.length + 1);
        }
        // cursor was in middle
        else if (e.currentTarget.selectionStart !== null && e.currentTarget.selectionStart < e.currentTarget.value.length) {
            let currString = "";
            const cleanSubString = inputTyped.substring(0, e.currentTarget.selectionStart).replace(/(\s|\(|\)|-)/g, "");
            let j = 0;
            for (let i = 0; i < formattedVersion.length; i++) {
                if (/\d/.test(formattedVersion[i]) && cleanSubString[j] === formattedVersion[i]) {
                    currString += formattedVersion[i];
                    j++
                }
                if (currString === cleanSubString) {
                    setCursor(i + 1);
                    break;
                }
            }

        }
        const digitCount = newInputValue.replace(/\D/g, '').length;
        if (digitCount <= 15) {
            setValue(newInputValue);
        }
    }, [countryCallingCode, countryCode, setValue, currLength]);

    return (
        <input
            style={{
                fontFamily: "Goldman Sans",
                fontSize: "14px",
                paddingLeft: "10px"
            }}
            disabled={disabled}
            id={name}
            ref={inputRef}
            className={
                `outline-0 antialiased flex-grow ${
                    disabled ? 'bg-gray-10 border-gray-20 text-gray-40' : focused ? "border-blue-60" : "border-gray-50"
                } border-r border-t border-b border-l flex items-center h-8 rounded-sm`
            }
            onChange={handleOnChange}
            name={name}
            onBlur={e => {
                setFocused(false);
                if (onBlur)
                    onBlur(e);
            }}
            onFocus={() => setFocused(true)}
            value={inputValue}
        />
    );
}, (prevProps, nextProps) => {
    return prevProps.inputValue === nextProps.inputValue &&
        prevProps.onBlur === nextProps.onBlur &&
        prevProps.setFocused === nextProps.setFocused &&
        prevProps.name === nextProps.name &&
        prevProps.setValue === nextProps.setValue &&
        prevProps.countryCallingCode === nextProps.countryCallingCode &&
        prevProps.countryCode === nextProps.countryCode
});